import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { onboardingApi, simulationApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Zap, ArrowRight, ArrowLeft, Loader2, DollarSign, Shield, Bot, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/utils';

const STEPS = [
  { id: 'budget', title: 'Budget Setup', icon: DollarSign },
  { id: 'risk', title: 'Risk Profile', icon: Shield },
  { id: 'categories', title: 'Strategy Categories', icon: Bot },
  { id: 'treasury', title: 'Treasury Config', icon: Wallet },
];

const CATEGORIES = [
  { id: 'ecommerce', label: 'E-commerce', description: 'Dropshipping, niche stores, CRO' },
  { id: 'content', label: 'Content & Audience', description: 'SEO, newsletters, affiliate' },
  { id: 'trading', label: 'Trading & Markets', description: 'Paper trading, arbitrage (simulation)' },
  { id: 'digital_product', label: 'Digital Products', description: 'Courses, SaaS validation' },
  { id: 'outreach', label: 'Outreach & Sales', description: 'B2B leads, cold outreach' },
];

export const Onboarding = () => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const { updateUser } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    total_budget: 100000,
    risk_profile: 'balanced',
    allowed_categories: ['ecommerce', 'content', 'digital_product'],
    jurisdictions: ['US'],
    preferred_currencies: ['USD'],
    daily_loss_limit: 1000,
    weekly_loss_limit: 5000,
    operation_mode: 'simulation',
    max_capital_per_strategy: 15000,
    reserve_capital: 25000,
  });

  const updateForm = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleCategory = (categoryId) => {
    setFormData((prev) => ({
      ...prev,
      allowed_categories: prev.allowed_categories.includes(categoryId)
        ? prev.allowed_categories.filter((c) => c !== categoryId)
        : [...prev.allowed_categories, categoryId],
    }));
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onboardingApi.complete(formData);
      
      // Seed demo data
      try {
        await simulationApi.seedDemo();
      } catch (e) {
        console.log('Demo data may already exist');
      }
      
      updateUser({ onboarding_completed: true });
      toast.success('Welcome to Invictus!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base mb-4 block">Total Available Budget</Label>
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="number"
                    value={formData.total_budget}
                    onChange={(e) => updateForm('total_budget', Number(e.target.value))}
                    className="pl-10 text-lg font-mono bg-background"
                    data-testid="onboarding-budget"
                  />
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                This is simulation capital. No real money will be used.
              </p>
            </div>

            <div>
              <Label className="text-base mb-4 block">
                Daily Loss Limit: <span className="font-mono text-primary">{formatCurrency(formData.daily_loss_limit)}</span>
              </Label>
              <Slider
                value={[formData.daily_loss_limit]}
                onValueChange={([v]) => updateForm('daily_loss_limit', v)}
                max={formData.total_budget * 0.1}
                step={100}
                className="py-4"
                data-testid="onboarding-daily-limit"
              />
            </div>

            <div>
              <Label className="text-base mb-4 block">
                Weekly Loss Limit: <span className="font-mono text-primary">{formatCurrency(formData.weekly_loss_limit)}</span>
              </Label>
              <Slider
                value={[formData.weekly_loss_limit]}
                onValueChange={([v]) => updateForm('weekly_loss_limit', v)}
                max={formData.total_budget * 0.2}
                step={500}
                className="py-4"
                data-testid="onboarding-weekly-limit"
              />
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base mb-4 block">Risk Profile</Label>
              <RadioGroup
                value={formData.risk_profile}
                onValueChange={(v) => updateForm('risk_profile', v)}
                className="space-y-3"
              >
                <div className="flex items-center space-x-3 p-4 rounded border border-border bg-background hover:border-primary/30 transition-colors cursor-pointer">
                  <RadioGroupItem value="conservative" id="conservative" data-testid="risk-conservative" />
                  <Label htmlFor="conservative" className="flex-1 cursor-pointer">
                    <div className="font-semibold">Conservative</div>
                    <div className="text-sm text-muted-foreground">Slow and steady. Lower risk, smaller gains.</div>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-4 rounded border border-border bg-background hover:border-primary/30 transition-colors cursor-pointer">
                  <RadioGroupItem value="balanced" id="balanced" data-testid="risk-balanced" />
                  <Label htmlFor="balanced" className="flex-1 cursor-pointer">
                    <div className="font-semibold">Balanced</div>
                    <div className="text-sm text-muted-foreground">Moderate risk with diversified strategies.</div>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-4 rounded border border-border bg-background hover:border-primary/30 transition-colors cursor-pointer">
                  <RadioGroupItem value="aggressive" id="aggressive" data-testid="risk-aggressive" />
                  <Label htmlFor="aggressive" className="flex-1 cursor-pointer">
                    <div className="font-semibold">Aggressive</div>
                    <div className="text-sm text-muted-foreground">Higher risk tolerance. Faster scaling of winners.</div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label className="text-base mb-4 block">Operation Mode</Label>
              <Select
                value={formData.operation_mode}
                onValueChange={(v) => updateForm('operation_mode', v)}
              >
                <SelectTrigger className="bg-background" data-testid="operation-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="simulation">Simulation (Recommended)</SelectItem>
                  <SelectItem value="assisted">Assisted (Human approval required)</SelectItem>
                  <SelectItem value="live">Live (With strict limits)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-2">
                Start with simulation. You can switch to live mode later.
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <Label className="text-base mb-4 block">Select Strategy Categories</Label>
            {CATEGORIES.map((cat) => (
              <div
                key={cat.id}
                className="flex items-start space-x-3 p-4 rounded border border-border bg-background hover:border-primary/30 transition-colors cursor-pointer"
                onClick={() => toggleCategory(cat.id)}
              >
                <Checkbox
                  checked={formData.allowed_categories.includes(cat.id)}
                  onCheckedChange={() => toggleCategory(cat.id)}
                  data-testid={`category-${cat.id}`}
                />
                <div className="flex-1">
                  <div className="font-semibold">{cat.label}</div>
                  <div className="text-sm text-muted-foreground">{cat.description}</div>
                </div>
              </div>
            ))}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base mb-4 block">
                Reserve Capital (Untouchable): <span className="font-mono text-primary">{formatCurrency(formData.reserve_capital)}</span>
              </Label>
              <Slider
                value={[formData.reserve_capital]}
                onValueChange={([v]) => updateForm('reserve_capital', v)}
                max={formData.total_budget * 0.5}
                step={1000}
                className="py-4"
                data-testid="onboarding-reserve"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Capital that will never be allocated to agents.
              </p>
            </div>

            <div>
              <Label className="text-base mb-4 block">
                Max Capital per Strategy: <span className="font-mono text-primary">{formatCurrency(formData.max_capital_per_strategy)}</span>
              </Label>
              <Slider
                value={[formData.max_capital_per_strategy]}
                onValueChange={([v]) => updateForm('max_capital_per_strategy', v)}
                max={formData.total_budget * 0.3}
                step={500}
                className="py-4"
                data-testid="onboarding-max-per-strategy"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Maximum allocation for any single agent strategy.
              </p>
            </div>

            <div className="p-4 rounded bg-primary/10 border border-primary/20">
              <h4 className="font-semibold text-primary mb-2">Summary</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Total Budget:</span>
                <span className="font-mono">{formatCurrency(formData.total_budget)}</span>
                <span className="text-muted-foreground">Available for Agents:</span>
                <span className="font-mono">{formatCurrency(formData.total_budget - formData.reserve_capital)}</span>
                <span className="text-muted-foreground">Risk Profile:</span>
                <span className="capitalize">{formData.risk_profile}</span>
                <span className="text-muted-foreground">Mode:</span>
                <span className="capitalize">{formData.operation_mode}</span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="absolute inset-0 grid-pattern opacity-20" />

      <div className="w-full max-w-2xl relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded bg-primary flex items-center justify-center">
              <Zap className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-xl">Invictus</span>
          </div>
          <h1 className="font-heading text-2xl font-bold mb-2">Set Up Your Operation</h1>
          <p className="text-muted-foreground">Configure your autonomous profit-seeking system</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <React.Fragment key={s.id}>
                <div
                  className={`flex items-center gap-2 px-3 py-2 rounded ${
                    i === step
                      ? 'bg-primary/10 border border-primary/30 text-primary'
                      : i < step
                      ? 'bg-primary/5 text-primary'
                      : 'text-muted-foreground'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium hidden sm:inline">{s.title}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`w-8 h-0.5 ${i < step ? 'bg-primary' : 'bg-border'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading">{STEPS[step].title}</CardTitle>
            <CardDescription>Step {step + 1} of {STEPS.length}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderStep()}

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={step === 0}
                data-testid="onboarding-back"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              {step < STEPS.length - 1 ? (
                <Button
                  onClick={handleNext}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="onboarding-next"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="onboarding-complete"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Setting Up...
                    </>
                  ) : (
                    <>
                      Launch Swarm
                      <Zap className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
